import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import {
    AreaChart, Area, BarChart, Bar, LineChart, Line,
    XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie
} from 'recharts';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, TrendingUp, PieChart as PieIcon, BarChart3, Activity, ShieldCheck } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';

const COLORS = {
    emerald: '#10b981',
    amber: '#f59e0b',
    red: '#ef4444',
    blue: '#3b82f6',
    purple: '#8b5cf6',
    orange: '#f97316',
    slate: '#64748b'
};

const CATEGORY_COLORS = {
    food: COLORS.orange,
    service: COLORS.blue,
    order_accuracy: COLORS.emerald,
    value: COLORS.amber,
    cleanliness: '#06b6d4',
    packaging: '#a855f7',
    wait_time: COLORS.red,
    amenities: '#14b8a6',
    other: COLORS.purple,
    __grouped__: COLORS.slate,
};

// The legend used to render EVERY category beside a fixed 200px chart, so a
// long tail pushed the card over its neighbours. Six slices plus one grouped
// "Other (n)" is the most a 390px card can show without truncating numbers.
const MAX_SLICES = 6;
const GROUPED_KEY = '__grouped__';

const RATING_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981'];

const CustomTooltip = ({ active, payload, label, valueLabel }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-lg text-sm">
            <p className="text-muted-foreground text-xs mb-1">{label}</p>
            {payload.map((entry, i) => (
                <p key={i} className="font-medium text-foreground">
                    <span style={{ color: entry.color }} className="mr-1.5">&#9679;</span>
                    {valueLabel || entry.name}: {entry.value}
                </p>
            ))}
        </div>
    );
};

const NoDataMessage = ({ message }) => (
    <div className="flex flex-col items-center justify-center h-full text-center py-8">
        <BarChart3 className="w-8 h-8 text-muted-foreground/30 mb-2" />
        <p className="text-sm text-muted-foreground">{message}</p>
    </div>
);

const ReviewShieldAnalytics = ({ organizationId, locationId, experienceType }) => {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState('30');

    useEffect(() => {
        loadAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeRange, organizationId, locationId, experienceType]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const params = { days: parseInt(timeRange) };
            if (organizationId && organizationId !== 'all') params.organization_id = organizationId;
            if (locationId && locationId !== 'all') params.location_id = locationId;
            // v4.38.0 — Honor the Experience-Type filter so charts +
            // trends + AI Insights cards reflect the operator's scope.
            if (experienceType && experienceType !== 'all') params.experience_type = experienceType;
            const res = await api.get('/review-shield/feedback/analytics', { params });
            setAnalytics(res.data);
        } catch (err) {
            console.error('Failed to load analytics:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDateLabel = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const hasData = analytics && analytics.total_in_period > 0;

    // Six biggest topics + ONE catch-all row. The canonical `other` bucket is
    // always folded into that same row, so the legend can never show two
    // "Other" lines. The backend already collapsed survey question text /
    // per-experience ids / the hotel "none" sentinel into canonical keys.
    const categorySlices = useMemo(() => {
        const rows = [...(analytics?.category_breakdown || [])]
            .filter((c) => c && c.count > 0)
            .sort((a, b) => b.count - a.count);
        const named = rows.filter((c) => c.category !== 'other');
        const other = rows.filter((c) => c.category === 'other');
        const head = named.slice(0, MAX_SLICES).map((c) => ({
            ...c,
            label: t(`reviewShield.improvementCategories.${c.category}`, c.category),
        }));
        const tail = [...named.slice(MAX_SLICES), ...other];
        if (!tail.length) return head;
        const count = tail.reduce((sum, c) => sum + c.count, 0);
        // A lone "Other" bucket is just "Other" — the (n) only makes sense
        // when several topics were folded together.
        const label = tail.length === 1 && tail[0].category === 'other'
            ? t('reviewShield.improvementCategories.other', 'Other')
            : t('reviewShield.categoryOtherGrouped', 'Other ({{n}})', { n: tail.length });
        return [...head, { category: GROUPED_KEY, count, label }];
    }, [analytics, t]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
            </div>
        );
    }

    const noDataMsg = t('reviewShield.noDataYet', 'No data available yet');

    return (
        <div className="space-y-4" data-testid="review-shield-analytics">
            {/* Analytics Header with Time Range Filter */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Activity className="w-5 h-5 text-emerald-500" />
                    {t('reviewShield.analyticsTitle', 'Analytics')}
                </h2>
                <Select value={timeRange} onValueChange={setTimeRange}>
                    <SelectTrigger className="w-[150px]" data-testid="analytics-time-range">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7">{t('reviewShield.last7Days', 'Last 7 days')}</SelectItem>
                        <SelectItem value="30">{t('reviewShield.last30Days', 'Last 30 days')}</SelectItem>
                        <SelectItem value="90">{t('reviewShield.last90Days', 'Last 90 days')}</SelectItem>
                        <SelectItem value="365">{t('reviewShield.allTime', 'All time')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Satisfaction Trend */}
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
                            <TrendingUp className="w-4 h-4 text-emerald-500" />
                            {t('reviewShield.satisfactionTrend', 'Satisfaction Trend')}
                        </div>
                        <div className="h-[200px]" data-testid="satisfaction-trend-chart">
                            {!hasData || !analytics.satisfaction_trend.length ? (
                                <NoDataMessage message={noDataMsg} />
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={analytics.satisfaction_trend}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                                        <XAxis
                                            dataKey="date"
                                            tickFormatter={formatDateLabel}
                                            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            domain={[0, 5]}
                                            ticks={[1, 2, 3, 4, 5]}
                                            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                                            axisLine={false}
                                            tickLine={false}
                                            width={30}
                                        />
                                        <Tooltip content={<CustomTooltip valueLabel="Avg Rating" />} />
                                        <Line
                                            type="monotone"
                                            dataKey="avg_rating"
                                            stroke={COLORS.emerald}
                                            strokeWidth={2.5}
                                            dot={{ r: 4, fill: COLORS.emerald, stroke: '#fff', strokeWidth: 2 }}
                                            activeDot={{ r: 6, fill: COLORS.emerald }}
                                            name="Avg Rating"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Rating Distribution */}
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
                            <BarChart3 className="w-4 h-4 text-amber-500" />
                            {t('reviewShield.ratingDistribution', 'Rating Distribution')}
                        </div>
                        <div className="h-[200px]" data-testid="rating-distribution-chart">
                            {!hasData || !analytics.rating_distribution.length ? (
                                <NoDataMessage message={noDataMsg} />
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={analytics.rating_distribution} barSize={36}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} vertical={false} />
                                        <XAxis
                                            dataKey="rating"
                                            tickFormatter={(v) => `${v}★`}
                                            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                                            axisLine={false}
                                            tickLine={false}
                                            width={30}
                                            allowDecimals={false}
                                        />
                                        <Tooltip content={<CustomTooltip valueLabel="Count" />} />
                                        <Bar dataKey="count" radius={[6, 6, 0, 0]} name="Count">
                                            {analytics.rating_distribution.map((entry, index) => (
                                                <Cell key={index} fill={RATING_COLORS[index]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Review Shield Outcomes — replaces the former Feedback Volume card */}
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
                            <ShieldCheck className="w-4 h-4 text-emerald-500" />
                            {t('reviewShield.outcomesTitle', 'Review Shield Results')}
                        </div>
                        <div className="h-[200px]" data-testid="review-shield-outcomes-card">
                            {!hasData || ((analytics?.reviews_generated ?? 0) + (analytics?.reviews_shielded ?? 0)) === 0 ? (
                                <NoDataMessage message={noDataMsg} />
                            ) : (
                                <div className="flex items-center h-full gap-4">
                                    <ResponsiveContainer width="55%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { name: t('reviewShield.reviewsGenerated', 'Reviews Generated'), value: analytics.reviews_generated },
                                                    { name: t('reviewShield.reviewsShielded', 'Reviews Shielded'), value: analytics.reviews_shielded },
                                                ]}
                                                dataKey="value"
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={45}
                                                outerRadius={75}
                                                strokeWidth={2}
                                                stroke="hsl(var(--background))"
                                                paddingAngle={2}
                                            >
                                                <Cell fill={COLORS.emerald} />
                                                <Cell fill={COLORS.red} />
                                            </Pie>
                                            <Tooltip content={<CustomTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="flex-1 space-y-3">
                                        <div className="flex items-center gap-2.5" data-testid="outcomes-generated">
                                            <div className="w-[15px] h-[15px] rounded-full shrink-0" style={{ backgroundColor: COLORS.emerald }} />
                                            <span className="text-[17px] text-foreground flex-1">{t('reviewShield.reviewsGenerated', 'Reviews Generated')}</span>
                                            <span className="text-[17px] font-semibold text-foreground">{analytics.reviews_generated}</span>
                                        </div>
                                        <div className="flex items-center gap-2.5" data-testid="outcomes-shielded">
                                            <div className="w-[15px] h-[15px] rounded-full shrink-0" style={{ backgroundColor: COLORS.red }} />
                                            <span className="text-[17px] text-foreground flex-1">{t('reviewShield.reviewsShielded', 'Reviews Shielded')}</span>
                                            <span className="text-[17px] font-semibold text-foreground">{analytics.reviews_shielded}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Category Breakdown */}
                <Card>
                    <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-3">
                            <PieIcon className="w-4 h-4 text-purple-500" />
                            {t('reviewShield.categoryBreakdown', 'Improvement Categories')}
                        </div>
                        {/* min-h, not h: the card GROWS instead of spilling over the
                            cards beside and below it. */}
                        <div className="min-h-[200px]" data-testid="category-breakdown-chart">
                            {!hasData || !categorySlices.length ? (
                                <NoDataMessage message={noDataMsg} />
                            ) : (
                                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                    <div className="h-[200px] w-full sm:w-[45%] sm:max-w-[200px] shrink-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={categorySlices}
                                                    dataKey="count"
                                                    nameKey="label"
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={45}
                                                    outerRadius={75}
                                                    strokeWidth={2}
                                                    stroke="hsl(var(--background))"
                                                >
                                                    {categorySlices.map((entry) => (
                                                        <Cell
                                                            key={entry.category}
                                                            fill={CATEGORY_COLORS[entry.category] || COLORS.slate}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(value, name) => [value, name]} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-2.5">
                                        {categorySlices.map((cat) => (
                                            <div key={cat.category} className="flex items-center gap-2.5"
                                                 data-testid={`category-legend-${cat.category}`}>
                                                <div
                                                    className="w-[15px] h-[15px] rounded-full shrink-0"
                                                    style={{ backgroundColor: CATEGORY_COLORS[cat.category] || COLORS.slate }}
                                                />
                                                {/* truncate + title: the full name is one tap/hover away */}
                                                <span className="text-[15px] text-foreground flex-1 min-w-0 truncate"
                                                      title={cat.label}>{cat.label}</span>
                                                <span className="text-[15px] font-semibold text-foreground shrink-0">{cat.count}</span>
                                            </div>
                                        ))}
                                        {analytics.uncategorized_count > 0 && (
                                            <p className="text-xs text-muted-foreground pt-1"
                                               data-testid="category-uncategorized-note">
                                                {t('reviewShield.categoryNoTopic', '{{n}} rated without naming a topic', { n: analytics.uncategorized_count })}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ReviewShieldAnalytics;
